import React, { useState, useEffect } from 'react';
import { useTitle } from '../hooks/useTitle';
import { LanguageSelector } from '../components/LanguageSelector';
import { AdminBar } from '../components/AdminBar';
import { Brain, ArrowRight, Award, ChevronRight } from 'lucide-react';

interface Question {
  id: number;
  text: string;
  options: {
    text: string;
    points: number;
  }[];
}

interface Result {
  range: [number, number];
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const questions: Question[] = [
  {
    id: 1,
    text: "Quel est votre degré de connaissance en intelligence artificielle ?",
    options: [
      { text: "Je ne sais pas vraiment ce que c'est et je n'ai jamais utilisé d'IA.", points: 0 },
      { text: "J'en ai entendu parler, mais je n'ai jamais réellement testé.", points: 1 },
      { text: "J'ai déjà testé quelques outils d'IA de manière occasionnelle.", points: 2 },
      { text: "J'utilise régulièrement l'IA et je commence à intégrer certains outils dans mon quotidien.", points: 3 },
      { text: "Je maîtrise pleinement l'IA et je l'utilise de manière avancée pour optimiser mon business.", points: 4 }
    ]
  },
  {
    id: 2,
    text: "Comment utilisez-vous l'IA dans votre activité ?",
    options: [
      { text: "Je ne l'utilise pas du tout.", points: 0 },
      { text: "Je m'en sers uniquement pour générer du contenu de manière sporadique.", points: 1 },
      { text: "J'automatise quelques tâches répétitives grâce à l'IA.", points: 2 },
      { text: "J'intègre l'IA dans plusieurs processus (communication, prospection, analyse de données).", points: 3 },
      { text: "Mon business repose sur une approche « IA-first » avec des process entièrement optimisés par l'IA.", points: 4 }
    ]
  },
  {
    id: 3,
    text: "Connaissez-vous et utilisez-vous des outils comme ChatGPT ou d'autres modèles de langage avancés ?",
    options: [
      { text: "Non, je ne connais pas ou je n'ai jamais testé.", points: 0 },
      { text: "Oui, j'ai déjà testé ChatGPT de manière occasionnelle.", points: 1 },
      { text: "J'utilise ChatGPT régulièrement en version gratuite.", points: 2 },
      { text: "J'utilise la version payante et j'ai appris à optimiser mes prompts.", points: 3 },
      { text: "J'intègre plusieurs solutions IA avancées (agents personnalisés, automatisation poussée, etc.) dans mes process.", points: 4 }
    ]
  },
  {
    id: 4,
    text: "Avez-vous déjà mis en place des workflows ou processus automatisés basés sur l'IA dans votre entreprise ?",
    options: [
      { text: "Non, rien n'est mis en place.", points: 0 },
      { text: "J'ai essayé quelques outils, sans réelle automatisation.", points: 1 },
      { text: "J'ai commencé à intégrer quelques processus automatisés pour gagner du temps.", points: 2 },
      { text: "J'ai plusieurs workflows en place qui améliorent significativement ma productivité.", points: 3 },
      { text: "Toute mon organisation est structurée autour d'une utilisation stratégique et avancée de l'IA.", points: 4 }
    ]
  },
  {
    id: 5,
    text: "Quel est votre principal objectif en utilisant l'IA ?",
    options: [
      { text: "Découvrir ce qu'est l'IA.", points: 0 },
      { text: "Gagner du temps dans mes tâches quotidiennes.", points: 1 },
      { text: "Améliorer la qualité de mon contenu et de ma communication.", points: 2 },
      { text: "Automatiser et optimiser mes process pour augmenter ma rentabilité.", points: 3 },
      { text: "Transformer radicalement mon business avec une approche « IA-first ».", points: 4 }
    ]
  },
  {
    id: 6,
    text: "Comment continuez-vous à vous former ou approfondir vos connaissances sur l'IA ?",
    options: [
      { text: "Je n'ai pas encore commencé à me former.", points: 0 },
      { text: "Je lis quelques articles ou regarde des vidéos en ligne.", points: 1 },
      { text: "J'ai suivi des formations en ligne de base.", points: 2 },
      { text: "Je participe à des ateliers ou bootcamps pratiques.", points: 3 },
      { text: "J'investis dans des accompagnements personnalisés pour devenir un expert.", points: 4 }
    ]
  },
  {
    id: 7,
    text: "Quel est votre niveau de confiance quant à l'impact de l'IA sur votre business ?",
    options: [
      { text: "Je doute de son utilité.", points: 0 },
      { text: "Je pense que c'est une mode passagère.", points: 1 },
      { text: "Je vois son potentiel, mais je n'en ai pas encore tiré de bénéfice concret.", points: 2 },
      { text: "J'ai déjà constaté des gains de temps et d'argent grâce à l'IA.", points: 3 },
      { text: "Je suis convaincu que l'IA est un levier stratégique indispensable pour mon entreprise.", points: 4 }
    ]
  },
  {
    id: 8,
    text: "Dans quelle mesure optimisez-vous activement vos outils IA (par exemple, ajustement de prompts, création d'agents personnalisés, etc.) ?",
    options: [
      { text: "Je n'ai jamais essayé.", points: 0 },
      { text: "J'utilise les outils de base sans les modifier.", points: 1 },
      { text: "J'expérimente et ajuste occasionnellement.", points: 2 },
      { text: "J'optimise régulièrement pour améliorer mes résultats.", points: 3 },
      { text: "Je suis un véritable expert qui fait évoluer en continu mes stratégies IA.", points: 4 }
    ]
  }
];

const results: Result[] = [
  {
    range: [0, 8],
    title: "Niveau Débutant",
    description: "Vous êtes encore en phase de découverte de l'IA. Il y a de grandes opportunités à explorer pour gagner en efficacité.",
    icon: <Brain className="w-12 h-12 text-blue-500" />,
    color: "blue"
  },
  {
    range: [9, 16],
    title: "Niveau Intermédiaire",
    description: "Vous avez commencé à intégrer l'IA dans certains aspects de votre activité, mais il reste de la marge pour optimiser et structurer votre usage.",
    icon: <Brain className="w-12 h-12 text-green-500" />,
    color: "green"
  },
  {
    range: [17, 24],
    title: "Niveau Avancé",
    description: "Vous utilisez l'IA de manière régulière et efficace. Vous optimisez vos processus et commencez à en faire un véritable levier de croissance.",
    icon: <Brain className="w-12 h-12 text-purple-500" />,
    color: "purple"
  },
  {
    range: [25, 32],
    title: "Niveau Expert / IA-First",
    description: "Vous maîtrisez l'IA et votre business s'appuie largement sur des stratégies avancées d'automatisation et d'optimisation. Vous êtes à la pointe de l'innovation.",
    icon: <Brain className="w-12 h-12 text-orange-500" />,
    color: "orange"
  }
];

export function AiQuiz() {
  const { setTitle } = useTitle();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [result, setResult] = useState<Result | null>(null);

  useEffect(() => {
    setTitle('Quiz IA - Évaluez votre niveau');
  }, [setTitle]);

  const handleAnswer = (points: number) => {
    const newAnswers = [...answers, points];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      const totalScore = newAnswers.reduce((a, b) => a + b, 0);
      setScore(totalScore);
      const matchingResult = results.find(
        r => totalScore >= r.range[0] && totalScore <= r.range[1]
      );
      setResult(matchingResult || null);
      setShowResult(true);
    }
  };

  const progressPercentage = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-white">
      <LanguageSelector />
      <AdminBar />

      <div className="bg-gradient-to-b from-orange-50 to-white">
        <div className="container mx-auto px-4 py-16">
          <h1 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-6 animate-slideUp">
            Quiz : Quel est votre niveau en IA ?
          </h1>
          <p className="text-xl text-center text-gray-600 max-w-2xl mx-auto mb-12 animate-slideUp">
            Évaluez votre maîtrise de l'intelligence artificielle et découvrez comment optimiser son utilisation pour votre business
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          {!showResult ? (
            <div className="bg-white rounded-xl shadow-lg p-8 animate-slideUp">
              {/* Progress Bar */}
              <div className="mb-8">
                <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-orange-500 transition-all duration-300 ease-out"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
                <div className="mt-2 text-sm text-gray-600 text-right">
                  Question {currentQuestion + 1} sur {questions.length}
                </div>
              </div>

              {/* Question */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-8">
                  {questions[currentQuestion].text}
                </h2>
                <div className="space-y-4">
                  {questions[currentQuestion].options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswer(option.points)}
                      className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-orange-500 hover:bg-orange-50 transition-all flex items-center justify-between group"
                    >
                      <span>{option.text}</span>
                      <ChevronRight className="w-5 h-5 text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : result ? (
            <div className="bg-white rounded-xl shadow-lg p-8 animate-slideUp">
              <div className="text-center mb-8">
                <div className="inline-block p-3 rounded-full bg-orange-100 mb-4">
                  <Award className="w-12 h-12 text-orange-500" />
                </div>
                <h2 className="text-3xl font-bold mb-4">{result.title}</h2>
                <p className="text-xl text-gray-600 mb-8">{result.description}</p>
                <div className="text-2xl font-bold text-orange-500 mb-4">
                  Score : {score} / 32
                </div>
              </div>

              <div className="space-y-6">
                <div className="p-6 bg-orange-50 rounded-lg">
                  <h3 className="font-bold mb-2">Prochaines étapes recommandées :</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <ArrowRight className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                      <span>Réservez une session stratégique gratuite pour identifier vos opportunités d'optimisation</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <ArrowRight className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                      <span>Participez à notre prochain bootcamp IA pour monter en compétence rapidement</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <ArrowRight className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                      <span>Explorez notre catalogue d'outils IA pour découvrir les solutions adaptées à vos besoins</span>
                    </li>
                  </ul>
                </div>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => window.open('https://tidycal.com/quentin.tousart/novster-ai', '_blank')}
                    className="bg-orange-500 text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-orange-600 transition-colors"
                  >
                    Réserver ma session stratégique
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}